import { supabase } from '@/utils/supabase'
import { PLATFORM_TABLE_MAP } from '@/utils/security'

function getOrdersTable(platform) {
  return PLATFORM_TABLE_MAP[platform]?.orders || 'orders'
}

export async function checkMonthlyCompletedOrders(userId, platform = 'mobile_delta') {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)

  const ordersTable = getOrdersTable(platform)
  const { data, error } = await supabase
      .from(ordersTable)
      .select('id')
      .eq('handler_id', userId)
      .in('status', ['completed', 'finished'])
      .gte('finished_at', startOfMonth.toISOString())
      .lte('finished_at', endOfMonth.toISOString())

  if (error) {
    console.error('获取月完成订单数失败:', error)
    return { count: 0, error }
  }

  return { count: data?.length || 0, error: null }
}

export async function checkEventRegistration(eventId, userId) {
  const { data, error } = await supabase
    .from('event_players')
    .select('*')
    .eq('event_id', eventId)
    .eq('player_id', userId)

  if (error) {
    console.error('检查报名状态失败:', error)
    return { isRegistered: false, error }
  }

  return { isRegistered: data?.length > 0, data: data?.[0] || null, error: null }
}

export async function registerForEvent(eventId, playerId, playerName) {
  const { data, error } = await supabase
    .from('event_players')
    .insert([
      {
        event_id: eventId,
        player_id: playerId,
        player_name: playerName,
        total_kills: 0,
        total_value: 0,
        weight_score: 0,
        round: 1,
        status: 'pending',
        created_at: new Date().toISOString()
      }
    ])
    .select()
    .single()

  if (error) {
    console.error('报名失败:', error)
    return { success: false, error }
  }

  return { success: true, data, error: null }
}

export async function getEventPlayers(eventId) {
  const { data, error } = await supabase
    .from('event_players')
    .select('*')
    .eq('event_id', eventId)
    .order('weight_score', { ascending: false })

  if (error) {
    console.error('获取参赛选手失败:', error)
    return { players: [], error }
  }

  return { players: data || [], error: null }
}

export async function updatePlayerStats(playerId, kills, value) {
  const weightScore = calculateWeight(kills, value)

  const { data, error } = await supabase
    .from('event_players')
    .update({
      total_kills: kills,
      total_value: value,
      weight_score: weightScore,
      updated_at: new Date().toISOString()
    })
    .eq('id', playerId)
    .select()
    .single()

  if (error) {
    console.error('更新选手数据失败:', error)
    return { success: false, error }
  }

  return { success: true, data, error: null }
}

export function calculateWeight(kills, value) {
  const killWeight = 1.5
  const valueWeight = 1.0
  return Math.floor(kills * killWeight + value * valueWeight)
}

export async function advanceRound(eventId) {
  const { players, error: fetchError } = await getEventPlayers(eventId)

  if (fetchError || players.length === 0) {
    return { success: false, error: fetchError }
  }

  const qualifiedPlayers = players.slice(0, Math.ceil(players.length / 2))

  const updates = qualifiedPlayers.map(player => ({
    id: player.id,
    round: player.round + 1,
    status: 'qualified'
  }))

  for (const update of updates) {
    await supabase
      .from('event_players')
      .update({ round: update.round, status: update.status })
      .eq('id', update.id)
  }

  const eliminatedPlayers = players.slice(Math.ceil(players.length / 2))
  for (const player of eliminatedPlayers) {
    await supabase
      .from('event_players')
      .update({ status: 'eliminated' })
      .eq('id', player.id)
  }

  return { success: true, qualifiedCount: qualifiedPlayers.length }
}
